import { z } from 'zod';

/**
 * A Mintroute id the operator types: NaN while empty. NaN must parse (plain
 * `z.number()` rejects it), or an unused Mintroute block would block every
 * other provider's row; the Mintroute rule below enforces the ids.
 */
const mintrouteId = z.number().or(z.nan());

/**
 * Mintroute's catalogue details for a mapping (UT system `MintrouteDetails`).
 * The ids are Mintroute's own, from its catalogue; wallet defaults to USD and
 * VAT to NO. Category is text pre-filled with our brand's category and can be
 * changed. Brand and denomination are copied from ours and the contract price
 * is the cost, so those are not entered here.
 */
export const mintrouteDetailsSchema = z.object({
  /**
   * Free text, stored as both Category and CategoryId (e.g. "Gaming"), exactly as the
   * operator leaves it. Pre-filled with our brand's category but not checked against
   * our categories — Mintroute's may differ. Not validated.
   */
  category: z.string(),
  denominationId: mintrouteId,
  brandId: mintrouteId,
  wallet: z.string().trim(),
  vat: z.enum(['NO', 'YES']),
});

export type MintrouteDetailsValues = z.infer<typeof mintrouteDetailsSchema>;

export const EMPTY_MINTROUTE_DETAILS: MintrouteDetailsValues = {
  category: '',
  denominationId: Number.NaN,
  brandId: Number.NaN,
  wallet: 'USD',
  vat: 'NO',
};

const MINTROUTE_ID_LABELS: Record<'denominationId' | 'brandId', string> = {
  denominationId: 'denomination',
  brandId: 'brand',
};

/**
 * One provider mapping, mirroring the Admin API. Input type === output type.
 *
 * `identifierRequired` and `integration` are not sent: they are copied from the
 * chosen provider when it is picked (`Provider.productIdentifierRequired`,
 * `Provider.integration`), so the schema enforces the provider's rules without
 * knowing about providers. Mintroute buys by EAN and LikeCard by its product id;
 * an order routed to a mapping without one fails (UT system `ProviderIntegrations`).
 * Mintroute mappings also need Mintroute's ids (`mintroute`).
 */
export const mappingSchema = z
  .object({
    /** 0 = not chosen yet (create only). */
    vendorId: z.number().int().positive('Select a provider.'),
    cost: z
      .number({ message: 'Enter a cost.' })
      .min(0, 'A cost cannot be negative.')
      .max(1_000_000, 'Too large.'),
    productIdentifier: z.string().trim().max(100, 'Must be 100 characters or fewer.'),
    isActive: z.boolean(),
    identifierRequired: z.boolean(),
    integration: z.enum(['Mintroute', 'LikeCard']).nullable(),
    mintroute: mintrouteDetailsSchema,
  })
  .superRefine((values, context) => {
    if (values.identifierRequired) {
      const identifier = values.productIdentifier.trim();
      if (identifier === '') {
        context.addIssue({
          code: 'custom',
          path: ['productIdentifier'],
          message: 'Required — orders are bought from this provider with it.',
        });
      } else if (!/^\d+$/.test(identifier)) {
        context.addIssue({ code: 'custom', path: ['productIdentifier'], message: 'Digits only.' });
      }
    }

    if (values.integration !== 'Mintroute') return;
    for (const key of ['denominationId', 'brandId'] as const) {
      const id = values.mintroute[key];
      if (!Number.isInteger(id) || id <= 0) {
        context.addIssue({
          code: 'custom',
          path: ['mintroute', key],
          message: `Enter Mintroute's ${MINTROUTE_ID_LABELS[key]} id.`,
        });
      }
    }
    if (!/^[A-Za-z]{3}$/.test(values.mintroute.wallet)) {
      context.addIssue({
        code: 'custom',
        path: ['mintroute', 'wallet'],
        message: 'Use a 3-letter currency code, e.g. USD.',
      });
    }
  });

export type MappingFormValues = z.infer<typeof mappingSchema>;

/** One or more provider mappings: several when creating, one when editing. */
export const mapProvidersSchema = z
  .object({ items: z.array(mappingSchema).min(1) })
  .superRefine((values, context) => {
    const seen = new Set<number>();
    values.items.forEach((item, index) => {
      if (item.vendorId > 0 && seen.has(item.vendorId)) {
        context.addIssue({
          code: 'custom',
          path: ['items', index, 'vendorId'],
          message: 'This provider is already in the list.',
        });
      }
      seen.add(item.vendorId);
    });
  });

export type MapProvidersValues = z.infer<typeof mapProvidersSchema>;

/** Balance adjustment: a non-zero amount and a reason (e.g. a transfer reference). */
export const balanceAdjustmentSchema = z.object({
  amount: z
    .number({ message: 'Enter an amount.' })
    .refine((value) => value !== 0, 'Enter a non-zero amount.'),
  reason: z
    .string()
    .trim()
    .min(3, 'Give a reason, e.g. the transfer reference.')
    .max(500, 'Must be 500 characters or fewer.'),
});

export type BalanceAdjustmentValues = z.infer<typeof balanceAdjustmentSchema>;

export const balanceSettingsSchema = z.object({
  isBalanceConsidered: z.boolean(),
  /** NaN = no alarm (the input is empty). */
  lowBalanceThreshold: z.number().min(0, 'Cannot be negative.').or(z.nan()),
});

export type BalanceSettingsValues = z.infer<typeof balanceSettingsSchema>;

/** A new provider: its name, currency and balance settings (the Admin API re-checks both). */
export const createProviderSchema = balanceSettingsSchema.extend({
  name: z
    .string()
    .trim()
    .min(2, 'Enter the provider name.')
    .max(100, 'Must be 100 characters or fewer.'),
  currencyCode: z.string().regex(/^[A-Za-z]{3}$/, 'Use the three-letter ISO code, e.g. USD.'),
});

export type CreateProviderValues = z.infer<typeof createProviderSchema>;
